import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  Edit,
  Delete,
  LocationOn,
  Email,
  Phone,
  Download,
} from "@mui/icons-material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";

import {
  getFabricasByProveedor,
  getFabricasNoAsociadas,
  asociarFabricaAProveedor,
  deleteRelacionProveedorFabrica,
} from "../../api/ProveedoresyFabricas/fabricasApi";

import { getDocumentosByFabrica } from "../../api/ProveedoresyFabricas/documentosFabricasApi";

import ProveedoresFabricasDrawer from "./ProveedoresFabricasDrawer";
import ProveedoresFabricasAddDrawer from "./ProveedoresFabricasAddDrawer";

const FACTORIES_HEADER = "#ffac7e";

const isImageDoc = (archivo) =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(archivo || ""));

const ProveedoresFabricas = ({ proveedor }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [fabricas, setFabricas] = useState([]);
  const [fabricasDisponibles, setFabricasDisponibles] = useState([]);
  const [fabricaSeleccionada, setFabricaSeleccionada] = useState("");

  const [confirmDelete, setConfirmDelete] = useState(null);

  /* abrir drawer Fabricas */
  const [selectedFabrica, setSelectedFabrica] = useState(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  /* abrir drawer Fabricas */

  /* abrir drawer Añadir Fabricas */
  const [isAddDrawerOpen, setAddDrawerOpen] = useState(false);
  /* abrir drawer Añadir Fabricas */

  /* Documentos */
  const [documentos, setDocumentos] = useState({});
  const [loadingDocs, setLoadingDocs] = useState({});
  /* Documentos */

  useEffect(() => {
    if (proveedor) {
      fetchFabricas();
      fetchFabricasNoAsociadas();
    }
  }, [proveedor]);

  const fetchFabricas = async () => {
    try {
      const data = await getFabricasByProveedor(proveedor.CODPROV);
      setFabricas(data);

      // Cargar documentos para cada fábrica
      data.forEach((fabrica) => fetchDocumentos(fabrica.id_Fabrica));
    } catch (error) {
      console.error("❌ Error al obtener fábricas:", error);
    }
  };

  const fetchDocumentos = async (id_Fabrica) => {
    setLoadingDocs((prev) => ({ ...prev, [id_Fabrica]: true }));

    try {
      const docs = await getDocumentosByFabrica(id_Fabrica);
      setDocumentos((prev) => ({ ...prev, [id_Fabrica]: docs }));
    } catch (error) {
      console.error("❌ Error al obtener documentos:", error);
    } finally {
      setLoadingDocs((prev) => ({ ...prev, [id_Fabrica]: false }));
    }
  };

  /* abrir drawer Fabricas */
  const handleEditFabrica = (fabrica) => {
    setSelectedFabrica(fabrica);
    setDrawerOpen(true);
  };

  const handleSaveFabrica = async (updatedFabrica) => {
    await fetchFabricas(); // ✅ Recargar datos después de actualizar
    setDrawerOpen(false);
  };
  /*  abrir drawer Fabricas */

  const fetchFabricasNoAsociadas = async () => {
    try {
      const data = await getFabricasNoAsociadas(proveedor.CODPROV);
      setFabricasDisponibles(data);
    } catch (error) {
      console.error("❌ Error al obtener fábricas no asociadas:", error);
    }
  };

  // 🔥 Muestra el diálogo de confirmación antes de eliminar
  const handleOpenConfirmDelete = (id_Fabrica) => {
    setConfirmDelete(id_Fabrica);
  };

  // ❌ Eliminar relación proveedor-fábrica
  const handleDelete = async () => {
    if (!confirmDelete) return;

    try {
      await deleteRelacionProveedorFabrica(proveedor.CODPROV, confirmDelete); // ✅ Pasar CODPROV e id_Fabrica
      setFabricas(fabricas.filter((fab) => fab.id_Fabrica !== confirmDelete));
      fetchFabricasNoAsociadas(); // 🔄 Refrescar lista de fábricas disponibles
    } catch (error) {
      console.error(
        "❌ Error al eliminar la relación proveedor-fábrica:",
        error
      );
    }

    setConfirmDelete(null); // Cerrar el diálogo
  };

  const handleAsociarFabrica = async () => {
    if (!fabricaSeleccionada) return;

    try {
      await asociarFabricaAProveedor(proveedor.CODPROV, fabricaSeleccionada);
      fetchFabricas(); // 🔄 Recargar la lista
      fetchFabricasNoAsociadas(); // 🔄 Actualizar disponibles
      setFabricaSeleccionada(""); // 🔄 Reset selección
    } catch (error) {
      console.error("❌ Error al asociar fábrica:", error);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: 0,
        maxWidth: 440,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: FACTORIES_HEADER,
          px: 1.5,
          py: 1,
          borderRadius: 1.5,
          zIndex: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: "#fff",
            fontWeight: 700,
          }}
        >
          {t("ProveedoresPage.factoriesTitle")}
        </Typography>

        <Tooltip title={t("ProveedoresPage.associateFactory")}>
          <IconButton
            size="small"
            aria-label={t("ProveedoresPage.associateFactory")}
            sx={{
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.6)",
              "&:hover": {
                backgroundColor: "#90b2b7",
                color: "#fff",
              },
            }}
            onClick={() => setAddDrawerOpen(true)}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {fabricas.length === 0 ? (
        <Typography
          color="text.secondary"
          sx={{ mt: 1.5, px: 0.5, fontStyle: "italic" }}
        >
          {t("ProveedoresPage.noFactoriesYet")}
        </Typography>
      ) : (
        <List
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            mt: 1,
            py: 0,
            pt: 0.7,
            "& .MuiListItem-root": { paddingLeft: "4px", px: 0 },
          }}
        >
          {fabricas.map((fabrica) => (
            <React.Fragment key={fabrica.id_Fabrica}>
              <ListItem sx={{ pt: 0, pr: 0 }}>
                <Paper
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "6px",
                    border: `1px solid ${theme.palette.divider}`,
                    width: "100%",
                    padding: "1em",
                    mt: 0.2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                    }}
                  >
                    {/* mandanga */}
                    <Box
                      sx={{
                        width: "85%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "start",
                          marginBottom: "1em",
                        }}
                      >
                        <FactoryOutlinedIcon
                          sx={{ color: "#FFAC7E", marginRight: "0.5em" }}
                        />

                        <Typography sx={{ mt: 0.5 }}>
                          <span
                            style={{
                              fontWeight: 600,
                              color: theme.palette.text.tercero,
                            }}
                          >
                            Factory:
                          </span>{" "}
                          {fabrica.NombreFabrica || "No disponible"}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "start",
                        }}
                      >
                        <LocationOn
                          sx={{ color: "#FFAC7E", marginRight: "0.5em" }}
                        />
                        <Typography sx={{ mb: 1 }}>
                          <span
                            style={{
                              fontWeight: 600,
                              color: theme.palette.text.tercero,
                            }}
                          >
                            Address:
                          </span>{" "}
                          {fabrica.Direccion || "No disponible"}
                        </Typography>
                      </Box>

                      <Typography sx={{ mb: 1, ml: 4.5 }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: theme.palette.text.tercero,
                          }}
                        >
                          Country:
                        </span>{" "}
                        {fabrica.Pais || "No disponible"}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "start",
                        }}
                      >
                        <Email
                          sx={{ color: "#90b2b7", marginRight: "0.5em" }}
                        />
                        <Typography sx={{ mb: 1 }}>
                          <span
                            style={{
                              fontWeight: 600,
                              color: theme.palette.text.tercero,
                            }}
                          >
                            Email:
                          </span>{" "}
                          {fabrica.Email || "No disponible"}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "start",
                        }}
                      >
                        <Phone
                          sx={{ color: "#34afc7", marginRight: "0.5em" }}
                        />
                        <Typography sx={{ mb: 1 }}>
                          <span
                            style={{
                              fontWeight: 600,
                              color: theme.palette.text.tercero,
                            }}
                          >
                            Phone:
                          </span>{" "}
                          {fabrica.Telefono || "No disponible"}
                        </Typography>
                      </Box>

                      <Typography sx={{ mb: 1, ml: 4.5 }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: theme.palette.text.tercero,
                          }}
                        >
                          Notas:
                        </span>{" "}
                        {fabrica.Notas || "No disponible"}
                      </Typography>
                    </Box>
                    {/* iconos editar eliminar */}
                    <Box
                      sx={{
                        width: "15%",
                        display: "flex",
                        alignItems: "start",
                        flexDirection: "column",
                      }}
                    >
                      <Tooltip title="Edit Factory">
                        <IconButton
                          sx={{
                            color: "#90b2b7",
                            "&.MuiIconButton-root": {
                              border: "1px solid #90b2b7",
                            },

                            "&.MuiButtonBase-root": {
                              height: "1.8em",
                              marginLeft: "5px",
                              marginTop: "6px",
                            },

                            "&:hover": {
                              backgroundColor: "#90b2b7",
                              color: "#fff",
                            },
                          }}
                          onClick={() => handleEditFabrica(fabrica)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete de Factory from this Supplier">
                        <IconButton
                          sx={{
                            color: "#90b2b7",
                            "&.MuiIconButton-root": {
                              border: "1px solid #90b2b7",
                            },

                            "&.MuiButtonBase-root": {
                              height: "1.8em",
                              marginLeft: "5px",
                              marginTop: "6px",
                            },

                            "&:hover": {
                              backgroundColor: "red",
                              color: "#fff",
                            },
                          }}
                          onClick={() => setConfirmDelete(fabrica.id_Fabrica)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Divider sx={{ mt: 1 }} />
                  {/* 📂 Documentos de la fábrica */}
                  <Box sx={{ mr: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{ mt: 2, fontWeight: 600, color: "#FFAC7E" }}
                    >
                      Documents:
                    </Typography>

                    {loadingDocs[fabrica.id_Fabrica] ? (
                      <CircularProgress size={20} sx={{ mt: 1 }} />
                    ) : documentos[fabrica.id_Fabrica]?.length > 0 ? (
                      <List dense>
                        {documentos[fabrica.id_Fabrica].map((doc) => (
                          <ListItem
                            key={doc.id_Documento}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between", // Separa el texto e icono
                              alignItems: "center", // Alinea verticalmente
                              ml: 2,
                              width: "100%",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                minWidth: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: 1,
                                  border: `1px solid ${theme.palette.divider}`,
                                  overflow: "hidden",
                                  bgcolor: theme.palette.background.default,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {isImageDoc(doc.Archivo) ? (
                                  <Box
                                    component="img"
                                    src={`${process.env.REACT_APP_API_URL}/uploads/fabricas/${doc.Archivo}`}
                                    alt=""
                                    sx={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <InsertDriveFileOutlinedIcon
                                    sx={{ fontSize: 18, color: "text.secondary" }}
                                  />
                                )}
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {doc.TipoDocumento} - Exp:{" "}
                                {new Date(
                                  doc.FechaCaducidad
                                ).toLocaleDateString()}
                              </Typography>
                            </Box>
                            <Tooltip title="Download Document">
                              <IconButton
                                component="a"
                                href={`${process.env.REACT_APP_API_URL}/uploads/fabricas/${doc.Archivo}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color: "#90b2b7",
                                  "&.MuiIconButton-root": {
                                    border: "1px solid #90b2b7",
                                  },

                                  "&.MuiButtonBase-root": {
                                    height: "1.8em",
                                    marginLeft: "5px",
                                    marginTop: "6px",
                                  },

                                  "&:hover": {
                                    backgroundColor: "#90b2b7",
                                    color: "#fff",
                                  },
                                }}
                              >
                                <Download />
                              </IconButton>
                            </Tooltip>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography color="textSecondary" sx={{ ml: 2, mt: 1 }}>
                        No documents available
                      </Typography>
                    )}
                    {/* 📂 Documentos de la fábrica */}
                  </Box>
                </Paper>
              </ListItem>

              {/*   <Divider /> */}
            </React.Fragment>
          ))}
        </List>
      )}

      <Box
        sx={{
          mb: 5,
        }}
      >
        {/* 🔽 Select para asociar fábricas */}
        <FormControl
          fullWidth
          sx={{
            /*    width: "94.5%", */
            display: "flex",
            margin: "0 auto",
            backgroundColor: theme.palette.background.paper,
            marginLeft: "0.5em",
            pr: 1,
          }}
        >
          <InputLabel>Select a factory</InputLabel>
          <Select
            value={fabricaSeleccionada}
            onChange={(e) => setFabricaSeleccionada(e.target.value)}
          >
            {fabricasDisponibles.map((fabrica) => (
              <MenuItem
                sx={{
                  "& .MuiSelect-outlined": {
                    display: "flex",
                    alignItems: "center",
                  },
                }}
                key={fabrica.id_Fabrica}
                value={fabrica.id_Fabrica}
              >
                {" "}
                <FactoryOutlinedIcon sx={{ color: "#FFAC7E", ml: 2, mr: 1 }} />
                {fabrica.NombreFabrica} | {fabrica.Pais}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          sx={{
            width: "98%",

            display: "flex",
            margin: "1em auto",
            backgroundColor: "#90b2b7",
            color: theme.palette.text.tercero,
            marginBottom: "2em",
            marginLeft: "0.5em",
            padding: "0.7em 0",
            mr: 2,
          }}
          onClick={handleAsociarFabrica}
          disabled={!fabricaSeleccionada}
        >
          Associate Factory
        </Button>
      </Box>

      {/* 🔥 Dialog de Confirmación */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Eliminar Fábrica</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Estás seguro de que deseas eliminar esta fábrica del proveedor?
            Esta acción no eliminará la fábrica de la base de datos, solo la
            desasociará de este proveedor.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔥 Drawer de edición */}
      <ProveedoresFabricasDrawer
        open={isDrawerOpen}
        onClose={() => setDrawerOpen(false)}
        fabrica={selectedFabrica}
        onSave={handleSaveFabrica}
      />

      {/* ✅ Drawer para añadir fábrica */}
      <ProveedoresFabricasAddDrawer
        open={isAddDrawerOpen}
        onClose={() => setAddDrawerOpen(false)}
        onSave={fetchFabricas} // Refresca la lista después de agregar
        proveedor={proveedor}
      />
    </Box>
  );
};

export default ProveedoresFabricas;
